import React, { useState, useContext, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import {
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Fade,
  IconButton,
  Avatar,
} from '@mui/material';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CloseIcon from '@mui/icons-material/Close';

import SideNavBar from './SideNavBar';
import SubNav from './SubNav';
import AddUserDialog from '../AddUserDialog';

import initioLogo from '../../images/initio-logo.png';

import {
  getFirstTimeAttribute,
  getUserInitials,
  getUserType,
  getUserEmail,
} from '../../utils/auth';

import { toTitleCase } from '../../utils';

import { NavWidthContext, ErrorMessageContext, IsSigningOutContext } from '../../contexts';

import useFetch from '../../hooks/useFetch';

import './NavBar.scss';

export default function NavBar() {
  const nav = useNavigate();
  const url = window.location.href;
  const { setIsSigningOut } = useContext(IsSigningOutContext);
  const { setShowErrorMessage } = useContext(ErrorMessageContext);
  const { navWidth } = useContext(NavWidthContext);

  const [tooltips, setTooltips] = useState([null, null]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [addingUser, setAddingUser] = useState(false);
  const [enterpriseCompanyName, setEnterpriseCompanyName] = useState('');
  const [currentUrl, setCurrentUrl] = useState(url);
  const [initial, setInitial] = useState('');

  useFetch({
    url: '/companies/enterprise/get-enterprise-company-info/',
    urlIds: ['companyId', 'userId'],
    onSuccess: (responseData) => {
      setEnterpriseCompanyName(responseData.companyName);
      localStorage.setItem('enterpriseCompanyInfo', JSON.stringify(responseData));
    },
  });

  async function handleLogout() {
    try {
      setIsSigningOut(true);
      await Auth.signOut();
      localStorage.removeItem('unifiedUser');
      window.location.replace(process.env.REACT_APP_AUTH_HOSTNAME);
    } catch (e) {
      setShowErrorMessage(toString(e));
    }
  }

  async function getFirstTimeExpAttribute() {
    let firstInitial = '';
    try {
      const userAuthData = await Auth.currentUserInfo();
      firstInitial = userAuthData?.attributes?.['custom:firstName']?.charAt(0) || '';
    } catch {
      firstInitial = '';
    }
    setInitial(toTitleCase(firstInitial || getUserInitials('firstInitial') || 'U'));
    const splitUrl = url.split('/');
    const currUrl = splitUrl[splitUrl.length - 1];
    setCurrentUrl(currUrl);
    const fTAttribute = getFirstTimeAttribute();
    if (
      (fTAttribute === '1' && currUrl !== 'first-enterprise-sign-in') &&
      (tooltips[0] === null && tooltips[1] === null)
    ) {
      setTooltips([true, true]);
    } else if ((tooltips[0] === false && tooltips[1] === false)) {
      try {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, { 'custom:firstTimeExp': '0' });
      } catch (e) {
        setShowErrorMessage(toString(e));
      }
    }
  }

  useEffect(() => { getFirstTimeExpAttribute(); }, [tooltips, url]);

  return (
    <>
      <div className="NavBar" style={{ minWidth: navWidth }}>
        {getUserType() !== 'investor' && (
          <SideNavBar
            tooltips={tooltips}
            setTooltips={setTooltips}
            currentUrl={currentUrl}
          />
        )}
        {getUserEmail().includes('@venbio.com') ? (
          <div className={`logo-container${getUserType() === 'investor' ? ' investor' : ''}`}>
            <img className="logo" src={initioLogo} alt="logo" />
          </div>
        ) : (
          <div className={`logo-container${getUserType() === 'investor' ? ' investor' : ''}`}>
            <img className="logo" src={initioLogo} alt="logo" />
            {/* <span>Powered by initio Software</span> */}
          </div>
        )}
        {getUserType() === 'investor' && (
          <div className="right-nav">
            <IconButton
              className={`profile-icon ${tooltips[0] ? 'first' : ''}`}
              onClick={(e) => {
                setMenuOpen(!menuOpen);
                setAnchorEl(e.currentTarget);
                setTooltips([tooltips[0] ? !tooltips[0] : false, tooltips[1]]);
              }}
            >
              <Avatar>{initial}</Avatar>
            </IconButton>
            <Menu
              id="nav-bar-dropdown"
              anchorEl={anchorEl}
              open={menuOpen}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              onClose={() => setMenuOpen(!menuOpen)}
              disableScrollLock
            >
              <MenuItem
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  handleLogout();
                }}
              >
                <LogoutOutlinedIcon />
                Sign out
              </MenuItem>
            </Menu>
          </div>
        )}
        {getUserType() !== 'investor' && (
          <div className="right-nav">
            {/*
            Search will be implemented when there is criteria to search
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search"
              />
            </Search> */}
            <Tooltip
              id="first-tooltip-right"
              open={tooltips[0] === null ? false : tooltips[0]}
              placement="bottom-end"
              arrow
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              title={(
                <>
                  <Button
                    onClick={() => {
                      setTooltips([false, tooltips[1]]);
                    }}
                  >
                    <CloseIcon />
                  </Button>
                  <p>Click your profile icon to:</p>
                  <ul>
                    <li>
                      add additional users from your
                      <br />
                      <b>company&apos;s page</b>
                      ,
                    </li>
                    <li>
                      set up multi-factor authentication
                      <br />
                      or change your password
                      <br />
                      from&thinsp;
                      <b>Profile & permissions</b>
                      ,
                    </li>
                    <li>sign out</li>
                  </ul>
                </>
              )}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <IconButton
                className={`profile-icon ${tooltips[0] ? 'first' : ''}`}
                onClick={(e) => {
                  setMenuOpen(!menuOpen);
                  setAnchorEl(e.currentTarget);
                  setTooltips([tooltips[0] ? !tooltips[0] : false, tooltips[1]]);
                }}
              >
                <Avatar>{initial}</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="nav-bar-dropdown"
              anchorEl={anchorEl}
              open={menuOpen}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              onClose={() => setMenuOpen(!menuOpen)}
              disableScrollLock
            >
              <MenuItem
                className={currentUrl === 'profile-settings' ? 'active' : ''}
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  nav('/profile-settings');
                }}
              >
                <AccountCircleOutlinedIcon />
                Profile & preferences
              </MenuItem>
              <MenuItem
                className={currentUrl === 'enterprise-info' ? 'active' : ''}
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  nav('/enterprise-info');
                }}
              >
                <DomainOutlinedIcon />
                {/* TODO Get Enterprise name from backend */}
                {(enterpriseCompanyName.length >= 20) ? `${enterpriseCompanyName.slice(0, 20).trim()}...` : enterpriseCompanyName}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  setAddingUser(true);
                }}
              >
                <PersonAddAltOutlinedIcon />
                Add User
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  handleLogout();
                }}
              >
                <LogoutOutlinedIcon />
                Sign out
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>
      <AddUserDialog addingUser={addingUser} setAddingUser={setAddingUser} />
      <SubNav />
    </>
  );
}
